import React, { memo, useCallback, useMemo, useState } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import { ClassNameMap, AlertColor, Alert, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import { FormattedMessage } from 'react-intl';
import { ApolloQueryResult } from '@apollo/client';
import { isEmpty } from 'lodash';
import { AllCollectorsQuery, Collector, CollectorType } from 'graphql/types';
import { useMessage } from 'modules/intl';
import { useSidePanelContext } from 'modules/rootPage';
import { ToastSnackBar } from 'modules/Settings/ToastSnackBar';
import { CollectorDetails } from './CollectorDetails';
import { CollectorInstallInfo } from './AllCollectorsTab';
import { CollectorCardComponent } from './CollectorCardComponent';
import { useCollectorSettingsUnified } from './hooks/useCollectorSettingsUnified';
import { settingObjCollector } from './utils';
import { useCollectorBroadcast } from './Install/hooks';
import { useCollectorSelectionContext } from './contexts/CollectorSelectionContextProvide';
import CollectorEmptyPageMessage from './ErrorPage/CollectorEmptyPageMessage';

const useStyles = makeStyles((theme) => ({
  input: {
    backgroundColor: theme.palette.grey[300],
    width: '80%',
    borderRadius: theme.spacing(0.5)
  },
  iconSearch: {
    color: theme.palette.grey[500],
    marginLeft: theme.spacing(1)
  },
  container: {
    margin: theme.spacing(2)
  },
  topContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    height: theme.spacing(4.5),
    marginLeft: theme.spacing(1)
  },
  select: {
    backgroundColor: theme.palette.common.white,
    width: '17%'
  },
  menuItem: {
    '&.Mui-selected': {
      backgroundColor: theme.palette.grey[200]!,
      '&:hover': {
        backgroundColor: theme.palette.grey[100]!
      }
    }
  },

  bottomContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%'
  },

  error: {
    display: 'flex',
    color: theme.palette.error.dark,
    padding: theme.spacing(0.5),
    margin: theme.spacing(0, 0, 0, 0)
  },
  errorIcon: {
    fontSize: theme.spacing(2.8),
    margin: theme.spacing(0.5, 0, 0, 0),
    color: theme.palette.error.dark
  },
  icon: {
    marginRight: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    height: theme.spacing(3),
    width: theme.spacing(3),
    alignSelf: 'flex-start'
  },
  emptyContainer: {
    marginTop: theme.spacing(-4),
    paddingBottom: theme.spacing(50)
  }
}));

type AllCollectorsListPageProps = {
  searchValue: string;
  data: AllCollectorsQuery | undefined;
  refetch: (variables?: any) => Promise<ApolloQueryResult<AllCollectorsQuery>>;
  hasError: boolean;
};

export type infoType = Collector & CollectorInstallInfo;

export const CollectorIconType: React.FC<{
  type: CollectorType | undefined;
  overrideClass?: ClassNameMap;
}> = ({
  type,
  overrideClass
}: {
  type: CollectorType | undefined;
  overrideClass?: ClassNameMap;
}) => {
  const classes = useStyles({ classes: overrideClass });
  if (type === CollectorType.Cloud)
    return (
      <CloudOutlinedIcon
        data-testid="collector-cloudIcon"
        className={classes.icon}
      />
    );
  else if (type === CollectorType.Bot)
    return (
      <ExtensionOutlinedIcon
        data-testid="collector-botIcon"
        className={classes.icon}
      />
    );
  else if (type === CollectorType.Desktop)
    return (
      <DesktopWindowsOutlinedIcon
        data-testid="collector-desktopIcon"
        className={classes.icon}
      />
    );
  else if (type === CollectorType.Plugin)
    return (
      <DesktopWindowsOutlinedIcon
        data-testid="collector-desktopIcon"
        className={classes.icon}
      />
    );
  return <></>;
};
export const AllCollectorsListPage: React.FC<AllCollectorsListPageProps> = memo(
  ({ searchValue, data, refetch, hasError }: AllCollectorsListPageProps) => {
    const {
      data: userSettingData,
      refetch: settingRefetch
    } = useCollectorSettingsUnified();

    const settingObj = useMemo(() => {
      const settings = settingObjCollector(userSettingData);
      return settings;
    }, [userSettingData]);
    const refetchQueries = useCallback(() => {
      refetch();
      settingRefetch();
    }, [refetch, settingRefetch]);

    useCollectorBroadcast({
      onBroadcast: refetchQueries
    });
    const classes = useStyles();
    const [openDialog, setOpenDialog] = useState(false);
    const { pinned } = useSidePanelContext();
    const [info, setInfo] = useState<infoType | null>(null);
    const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
    const { collectorsSelectionStatus } = useCollectorSelectionContext();

    const onSnackbarClose = useCallback(() => {
      setIsSnackBarOpen(false);
    }, []);
    const onSnackbarOpen = useCallback(() => {
      setIsSnackBarOpen(true);
    }, []);
    const disconnectSuccessMessage = useMessage(
      'collectorSetting.disconnectSuccessMessage'
    );

    const [value, setValue] = useState('collectors.overview');

    const collectorsList = useMemo(() => {
      if (searchValue.length === 0) {
        return {
          data: data?.collectors.items
        };
      } else {
        const dataCollectors = data?.collectors.items.filter((i) =>
          i.name.toLowerCase().includes(searchValue.toLowerCase())
        );

        return { data: dataCollectors || [] };
      }
    }, [data?.collectors.items, searchValue]);

    const handleOpenDialog = useCallback(
      (item) => {
        const i = { ...item, ...settingObj[item.id] };
        setInfo(i);
        setOpenDialog(true);
      },
      [settingObj]
    );

    const handleCloseDialog = useCallback(() => {
      setInfo(null);
      setValue('collectors.overview');
      setOpenDialog(false);
    }, []);

    const {
      installedCollectors,
      uninstalledCollectors,
      allCollectors
    } = useMemo(() => {
      const installedCollectors: Collector[] = [];
      const uninstalledCollectors: Collector[] = [];
      (collectorsList?.data || []).forEach((item) => {
        if ((item.installations?.items || []).length > 0) {
          installedCollectors.push(item);
        } else {
          uninstalledCollectors.push(item);
        }
      });
      return {
        installedCollectors: installedCollectors,
        uninstalledCollectors: uninstalledCollectors,
        allCollectors: collectorsList.data || []
      };
    }, [collectorsList.data]);

    const collectorsToDisplay = useMemo(() => {
      if (collectorsSelectionStatus === 'collectors.all') {
        return allCollectors;
      } else if (collectorsSelectionStatus === 'collectors.installed') {
        return installedCollectors;
      } else return uninstalledCollectors;
    }, [
      allCollectors,
      collectorsSelectionStatus,
      installedCollectors,
      uninstalledCollectors
    ]);
    const details = useMemo(() => {
      return (
        <CollectorDetails
          open={openDialog}
          handleClose={handleCloseDialog}
          info={info}
          value={value}
          setValue={setValue}
          refetch={refetch}
          onSnackBarOpen={onSnackbarOpen}
        />
      );
    }, [handleCloseDialog, info, onSnackbarOpen, openDialog, refetch, value]);

    return (
      <div
        className={classes.container}
        data-testid="Collector-All-Page-Container"
      >
        {hasError && (
          <Alert
            severity="error"
            icon={<ErrorIcon className={classes.errorIcon} />}
          >
            <div className={classes.error}>
              <Typography variant="caption">
                <FormattedMessage id="collectorError.errorNotification" />
              </Typography>
            </div>
          </Alert>
        )}
        <div
          className={classes.bottomContainer}
          data-testid="Collector-All-Page-Item"
        >
          {!isEmpty(collectorsToDisplay) ? (
            collectorsToDisplay.map((item, index) => (
              <CollectorCardComponent
                info={item}
                index={index}
                handleOpenDialog={handleOpenDialog}
                settingObj={settingObj}
                key={item.id}
              />
            ))
          ) : (
            <div className={classes.emptyContainer}>
              <CollectorEmptyPageMessage />
            </div>
          )}
          {details}
          <ToastSnackBar
            show={isSnackBarOpen}
            category={'success' as AlertColor}
            message={disconnectSuccessMessage}
            onClose={onSnackbarClose}
            pinned={pinned as boolean}
          />
        </div>
      </div>
    );
  }
);
AllCollectorsListPage.displayName = 'AllCollectorsListPage';
